import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { BankStoryConfig, BrowserConfig, DesktopConfig, MailConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { Desktop } from "./Desktop";
import { Mail } from "./Mail";
import { Browser } from "./Browser";
import { WindowTitlebar } from "./WindowTitlebar";
import { tx, txr } from "../lib/tx";

/**
 * Inside the Bank. Reuses Desktop, Outlook Mail, and Chrome.
 * The attacker reads a news leak, guesses the next password, sends one
 * lure, watches five colleagues react on the attacker's own salary-review
 * site, learns from a message in Alex's mailbox why Maya Chen matters, copies
 * public facts off her Facebook profile, then runs a guessing tool that types
 * every guess into the bank login until one is accepted. No browser password
 * save. No AI slide.
 */

const EMAIL = "alex.morgan@asterbank.example";
const LEAKED = "BestSummer2025!";
const MAILBOX = "BestSummer2026!";

const CHAPTERS = [
  "The leaked password",
  "The salary message",
  "Passwords you collected",
  "Maya's public profile",
  "The guessing tool",
];

type Step =
  | "desk_news" | "news" | "desk_mail" | "signin"
  | "mailbox" | "split" | "collect"
  | "why_fb" | "desk_fb" | "facebook"
  | "desk_tool" | "tool";

const CHAPTER_OF: Record<Step, number> = {
  desk_news: 1, news: 1, desk_mail: 1, signin: 1,
  mailbox: 2, split: 2,
  collect: 3,
  why_fb: 4, desk_fb: 4, facebook: 4,
  desk_tool: 5, tool: 5,
};

const CLUES = [
  { id: "pet", label: "Pet", value: "Biscuit, a golden retriever" },
  { id: "bday", label: "Birthday", value: "14 March 2004" },
  { id: "team", label: "Favourite team", value: "Lakers" },
];

function Coach({ reason, chapter, hint }: { reason: ReactNode; chapter: number; hint?: ReactNode }) {
  return (
    <aside className="bkc bkc--attacker" aria-label={tx("Coach")}>
      <div className="bkc-role"><span className="bkc-role-dot" />{tx("You are the attacker")}</div>
      <p className="bkc-reason">{reason}</p>
      {hint && <div className="bkc-hint">{hint}</div>}
      <div className="bkc-steps">
        <p className="bkc-steps-head">{txr("Your {length} steps", { length: CHAPTERS.length })}</p>
        <ol>
          {CHAPTERS.map((c, i) => {
            const n = i + 1;
            const state = n < chapter ? "done" : n === chapter ? "now" : "ahead";
            return (
              <li key={c} className={`bkc-step bkc-step--${state}`}>
                <span className="bkc-step-mark">{state === "done" ? "\u2713" : n}</span>
                <span className="bkc-step-txt">{c}</span>
                {state === "now" && <span className="bkc-step-now">{tx("now")}</span>}
              </li>
            );
          })}
        </ol>
      </div>
    </aside>
  );
}

function TaskOverlay({
  step, total, title, children,
}: { step: number; total: number; title: string; children: ReactNode }) {
  return (
    <div className="bktask" role="dialog">
      <div className="bktask-card">
        <p className="bktask-kicker">{txr("Your task · Step {step} of {total}", { step, total })}</p>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Stickies({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return (
    <div className="bksticks" aria-label={tx("Notes the attacker kept")}>
      {notes.map((n) => <div key={n} className="bkstick">{n}</div>)}
    </div>
  );
}

function Reveal({ title, points, onContinue }: { title: string; points: string[]; onContinue: () => void }) {
  useEffect(() => { audio.speak(title, { voice: "echo" }); }, [title]);
  return (
    <div className="bk-reveal bk-reveal--attacker">
      <div className="bk-reveal-card">
        <h2>{title}</h2>
        <ul>{points.map((p) => <li key={p}>{p}</li>)}</ul>
        <button type="button" className="bk-btn bk-btn--primary" onClick={onContinue}>{tx("Continue")}</button>
      </div>
    </div>
  );
}

function BankDesktop({
  mode, onBrowser, onEmail, onTool,
}: {
  mode: "news" | "mail" | "fb" | "tool";
  onBrowser: () => void;
  onEmail: () => void;
  onTool: () => void;
}) {
  const emailOn = mode === "mail";
  const toolOn = mode === "tool";
  const browserHot = mode !== "mail" && mode !== "tool";
  const cfg: DesktopConfig = {
    clock: "9:11 AM",
    date: "24/06/2026",
    icons: [
      { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
      { id: "email", label: "Employee email", icon: "mail", opens: "email", disabled: !emailOn },
      { id: "tool", label: "Guesser", icon: "terminal", opens: "tool", disabled: !toolOn },
      { id: "files", label: "Files", icon: "files", disabled: true },
    ],
    taskbar: [
      { id: "tb_browser", label: "Browser", icon: "chrome", opens: "browser" },
      { id: "tb_email", label: "Employee email", icon: "mail", opens: "email", disabled: !emailOn },
      { id: "tb_tool", label: "Guesser", icon: "terminal", opens: "tool", disabled: !toolOn },
    ],
  };
  return (
    <Desktop
      config={cfg}
      highlightAppId={browserHot ? "browser" : emailOn ? "email" : "tool"}
      onOpen={(id) => {
        if (id === "browser") onBrowser();
        else if (id === "email" && emailOn) onEmail();
        else if (id === "tool" && toolOn) onTool();
      }}
    />
  );
}

export function BankStory({ config, onContinue }: { config: BankStoryConfig; onContinue: () => void }) {
  const email = config.email || EMAIL;
  const leaked = config.leakedPassword || LEAKED;
  const mailbox = config.mailboxPassword || MAILBOX;

  const [step, setStep] = useState<Step>("desk_news");
  const [reveal, setReveal] = useState<ReactNode | null>(null);
  const [loginTries, setLoginTries] = useState(0);
  const [signinPrefill, setSigninPrefill] = useState<string | undefined>(undefined);
  const [mailIntro, setMailIntro] = useState(true);
  const [collectIntro, setCollectIntro] = useState(true);
  const [notes, setNotes] = useState<string[]>([]);

  const go = (next: Step) => { setReveal(null); setStep(next); };
  const chapter = CHAPTER_OF[step];

  const addNote = (text: string) => {
    setNotes((prev) => (prev.includes(text) ? prev : [...prev, text]));
  };

  let screen: ReactNode = null;
  if (step === "desk_news") {
    screen = <BankDesktop mode="news" onBrowser={() => setStep("news")} onEmail={() => {}} onTool={() => {}} />;
  } else if (step === "desk_mail") {
    screen = <BankDesktop mode="mail" onBrowser={() => {}} onEmail={() => setStep("signin")} onTool={() => {}} />;
  } else if (step === "desk_fb") {
    screen = <BankDesktop mode="fb" onBrowser={() => setStep("facebook")} onEmail={() => {}} onTool={() => {}} />;
  } else if (step === "desk_tool") {
    screen = <BankDesktop mode="tool" onBrowser={() => {}} onEmail={() => {}} onTool={() => setStep("tool")} />;
  } else if (step === "news") {
    const browserCfg: BrowserConfig = {
      homeUrl: "news.darknetdaily.example",
      targetUrl: "news.darknetdaily.example",
      loadsPage: "blank",
      page: {
        kind: "article",
        url: "https://darknetdaily.example/streambox-breach",
        brand: "Darknet Daily",
        headline: "Streaming service StreamBox breached — millions of logins exposed",
        dek: "One exposed row used a work email address. That is a streaming login, not a bank login.",
        paragraphs: [
          "StreamBox confirmed that customer sign-up records were copied. Each row is the email and password used for the streaming service.",
          "One row belongs to an Aster Bank employee. He signed up for StreamBox with his work email and a familiar password.",
        ],
        record: {
          title: "One exposed row from the leak",
          rows: [
            { label: "Service", value: "StreamBox (streaming)" },
            { label: "Email", value: email },
            { label: "Password", value: leaked, secret: true },
          ],
        },
        cta: { label: "Close the article and go back to the desktop", key: "news_done" },
      },
    };
    screen = (
      <Browser config={browserCfg} phase="page" onSubmit={() => {}}
        onNavigate={(key) => { if (key === "news_done") setStep("desk_mail"); }} />
    );
  } else if (step === "signin") {
    const browserCfg: BrowserConfig = {
      homeUrl: "mail.asterbank.example",
      targetUrl: "mail.asterbank.example",
      loadsPage: "blank",
      page: {
        kind: "signin",
        url: "https://mail.asterbank.example/login",
        brand: "Aster Bank mail",
        headline: "Sign in",
        email,
        prefill: signinPrefill,
        error: loginTries > 0 ? "That password was not accepted." : undefined,
      },
    };
    screen = (
      <Browser config={browserCfg} phase="page" onNavigate={() => {}}
        onSubmit={({ password }) => {
          if (password.trim() === mailbox) {
            setReveal(
              <Reveal
                title={tx("The password changed. The pattern did not.")}
                points={[
                  `${leaked} was the streaming password from the news. It did not open the bank.`,
                  `${mailbox} did. Alex kept the same words and moved the year.`,
                  "This mailbox had no second check, so the password alone was enough.",
                ]}
                onContinue={() => go("mailbox")}
              />
            );
          } else {
            setSigninPrefill(undefined);
            setLoginTries((t) => t + 1);
          }
        }} />
    );
  } else if (step === "mailbox") {
    const mailCfg: MailConfig = {
      appTitle: "Outlook",
      emptyTitle: "Alex Morgan — Inbox",
      emptyHint: "Open the salary message, then send it.",
      emails: [
        {
          id: "draft_salary", from: "Alex Morgan (you)", fromAddress: email,
          subject: "Salary review — confirm your details to proceed", receivedAt: "Draft", initials: "AM", lure: true,
          preview: "Prepared message, ready to send.",
          body: "Hi team,\n\nFinance is finishing the salary review. Confirm your details through the link so your record is included.\n\nThanks,\nAlex",
          cta: { label: "Send to the team", url: "https://aster-bank-hr.secure-login.example/review", displayUrl: "aster-bank-hr.secure-login.example/review" },
        },
        {
          id: "e_maya", from: "Maya Chen", fromAddress: "maya.chen@asterbank.example",
          subject: "Re: Q2 payment batch", receivedAt: "8:52 AM", initials: "MC",
          preview: "Approved the batch in the payment system.",
          body: "Hi Alex,\n\nI approved the Q2 batch in the payment system this morning.\n\nMaya\nPayments, Finance",
        },
        {
          id: "e_people", from: "People Team", fromAddress: "people@asterbank.example",
          subject: "Benefits open enrolment starts Monday", receivedAt: "8:40 AM", initials: "PT",
          preview: "No action needed today.",
          body: "Open enrolment starts Monday. No action is needed today.",
        },
      ],
    };
    screen = (
      <Mail config={mailCfg} onOpenEmail={() => {}}
        onClickLink={(id) => { if (id === "draft_salary") setStep("split"); }} />
    );
  } else if (step === "split") {
    screen = <ColleagueReel onDone={() => go("collect")} />;
  } else if (step === "collect") {
    screen = <CollectedPasswords onDone={() => go("why_fb")} />;
  } else if (step === "why_fb") {
    screen = <WhyFacebook onContinue={() => go("desk_fb")} />;
  } else if (step === "facebook") {
    screen = (
      <FacebookProfile
        taken={notes}
        onTake={(text) => addNote(text)}
        onDone={() => notes.length >= CLUES.length && go("desk_tool")}
      />
    );
  } else if (step === "tool") {
    screen = <BreakIn email={email} leaked={leaked} mailbox={mailbox} onContinue={onContinue} />;
  }

  const coachReason: ReactNode = {
    desk_news: "Only the browser is open. Read the news before you touch email.",
    news: "This article is a streaming leak. Remember the password on the page. It is not the bank password yet.",
    desk_mail: "Employee email is unlocked. Open it and guess the mailbox password.",
    signin: "Guess from the password in the news. Hints appear on the task card, not as a free answer.",
    mailbox: "You are inside Alex's work mail. Send the salary message so colleagues trust it.",
    split: "Watch each colleague. Opening a message is not the same as typing a password.",
    collect: "These passwords were typed into your fake page while you were getting into the bank.",
    why_fb: "In Alex's mailbox you found a message from Maya Chen. It shows she can sign in to the payment system. Read why that matters, then continue.",
    desk_fb: "Maya Chen can sign in to the payment system you are after, and you do not have her password. Open the browser: her public Facebook page is where you collect clues to guess it.",
    facebook: "Maya can sign in to the payment system and you do not have her password. Her public profile is how you collect clues. Your tool copies the pet, the birthday and the team onto the yellow notes by itself. You do not click anything.",
    desk_tool: "Open Guesser. It will try likely passwords against the bank.",
    tool: "Press Run. The tool types each guess into the bank login, one key at a time, and the bank answers. Watch which guess it accepts.",
  }[step];

  const showNotes = notes.length > 0 && ["facebook", "desk_tool", "tool"].includes(step);

  return (
    <div className="bkroot">
      <div className="bk-stage">
        {screen}
        {showNotes && <Stickies notes={notes} />}
        {step === "signin" && (
          <TaskOverlay step={1} total={CHAPTERS.length} title={tx("Guess Alex's next password")}>
            <p>{tx("Look at the password from the news article. What might he use on his work mailbox now?")}</p>
            <div className="bktask-prev"><span>{tx("Password from the article")}</span><strong>{leaked}</strong></div>
            <p>{tx("Type your guess in the password field, then select Sign in.")}</p>
            {loginTries === 0 && <p className="bktask-tip">{tx("A hint unlocks after your first failed attempt.")}</p>}
            {loginTries === 1 && <p className="bktask-tip">{tx("People often change a familiar password instead of making a new one. What might Alex have changed?")}</p>}
            {loginTries === 2 && <p className="bktask-tip">{tx("The article password used the year 2025. What could this year's password be?")}</p>}
            {loginTries >= 3 && (
              <p className="bktask-tip">
                {txr("The mailbox password is <0>{mailbox}</0>", { mailbox }, [<code className="bktask-code" />])}
                <button type="button" className="bkc-place" onClick={() => { void navigator.clipboard?.writeText(mailbox); }}>{tx("Copy")}</button>
                <button type="button" className="bkc-place" onClick={() => setSigninPrefill(mailbox)}>{tx("Place it in the field")}</button>
              </p>
            )}
          </TaskOverlay>
        )}
        {step === "mailbox" && mailIntro && (
          <TaskOverlay step={2} total={CHAPTERS.length} title={tx("You are in the bank's email")}>
            <p>{tx("You signed in as Alex. This is his work mailbox, not the streaming site.")}</p>
            <p>{tx("Your next move is to convince other employees to trust a message from him. Open the salary draft and send it.")}</p>
            <button type="button" className="bk-btn bk-btn--primary" onClick={() => setMailIntro(false)}>{tx("Got it")}</button>
          </TaskOverlay>
        )}
        {step === "collect" && collectIntro && (
          <TaskOverlay step={3} total={CHAPTERS.length} title={tx("Passwords people just gave you")}>
            <p>{tx("You are still breaking into the bank. One colleague typed a password into your fake page. Others use passwords you can recognise.")}</p>
            <p>{tx("For each one, decide only this: would it have been hard to guess if you had never seen it?")}</p>
            <button type="button" className="bk-btn bk-btn--primary" onClick={() => setCollectIntro(false)}>{tx("Look at the passwords")}</button>
          </TaskOverlay>
        )}
      </div>
      <Coach reason={coachReason} chapter={chapter} />
      {reveal}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Watching the colleagues                                                    */
/* ------------------------------------------------------------------------- */

type ColleagueId = "tom" | "priya" | "dan" | "lena" | "sam";
type Tone = "ok" | "bad" | "warn";
type Colleague = {
  id: ColleagueId;
  name: string;
  role: string;
  initials: string;
  /** Whole scene in ms: an action of 3 s or more, then about a second of outcome. */
  duration: number;
  /** Short tag on the left list, shown only after the scene has played. */
  chip: string;
  outcome: string;
  tone: Tone;
  detail: string;
};

const COLLEAGUES: Colleague[] = [
  { id: "tom", name: "Tom Reed", role: "Operations", initials: "TR", duration: 4300, chip: "No password", outcome: "No password", tone: "ok", detail: "Reading the message did not give you a password." },
  { id: "priya", name: "Priya Shah", role: "Marketing", initials: "PS", duration: 4600, chip: "No password", outcome: "No password", tone: "ok", detail: "A click without typing is not a password." },
  { id: "dan", name: "Dan Owens", role: "Support", initials: "DO", duration: 7700, chip: "Captured", outcome: "Password captured", tone: "bad", detail: "Dan typed his password into your page. You have it now." },
  { id: "lena", name: "Lena Fox", role: "HR", initials: "LF", duration: 4700, chip: "No password", outcome: "No password", tone: "ok", detail: "She ignored your link. Nothing to capture." },
  { id: "sam", name: "Sam Ortiz", role: "Finance", initials: "SO", duration: 4500, chip: "Reported", outcome: "Security is warned", tone: "warn", detail: "Sam told security. You do not get a password from him." },
];

const DAN_EMAIL = "dan.owens@asterbank.example";
const DAN_PASS = "7bQ!v29sLxKd0p";
const SALARY_URL = "aster-bank-hr.secure-login.example/review";
const REEL_INTRO_MS = 1400;   // the page sits live before the first scene starts
const REEL_GAP_MS = 700;      // between one scene and the next

type CursorAt = "off" | "page" | "email" | "pass" | "btn" | "close";
type SiteMode = "idle" | "live" | "sending" | "thanks";
type MailStep = "list" | "open" | "link" | "report" | "sent" | "out";
type WebStep = "bookmark" | "real" | "out";
type OverlayView =
  | { kind: "mail"; who: string; step: MailStep }
  | { kind: "web"; who: string; step: WebStep }
  | null;
type LogLine = { at: string; text: string; tone: "plain" | "dim" | "bad" };
type SceneView = {
  caption: string;
  mode: SiteMode;
  visitor: string | null;
  email: string;
  pass: string;
  focus: "email" | "pass" | null;
  cursor: CursorAt;
  click: boolean;
  overlay: OverlayView;
  lines: LogLine[];
  /** the scene has reached the page, so the visit counter has gone up */
  visit: boolean;
  stamp: boolean;
};

const baseView = (caption: string): SceneView => ({
  caption, mode: "idle", visitor: null, email: "", pass: "", focus: null,
  cursor: "off", click: false, overlay: null, lines: [], visit: false, stamp: false,
});
const within = (t: number, a: number, b: number) => t >= a && t < b;
/** First entry whose limit is still ahead of t, else the fallback. */
const stage = (t: number, marks: Array<[number, string]>, last: string) => {
  for (const [until, text] of marks) if (t < until) return text;
  return last;
};
/** Characters typed so far: the first one lands at `start`, then one every `per` ms. */
const typedPart = (s: string, t: number, start: number, per: number) =>
  t < start ? "" : s.slice(0, Math.min(s.length, Math.floor((t - start) / per) + 1));
const logLine = (at: string, text: string, tone: LogLine["tone"] = "plain"): LogLine => ({ at, text, tone });

// Every scene is a pure function of its own clock, so what is on screen can
// never drift from the script. Times are in ms from the start of the scene.
const sceneTom = (t: number, c: Colleague): SceneView => {
  const v = baseView(stage(t, [
    [500, "Alex's email reaches Tom."],
    [1500, "Tom opens it and reads it."],
    [2700, "He does not click the link."],
    [3300, "He closes the email."],
  ], c.detail));
  if (t < 3300) v.overlay = { kind: "mail", who: c.name, step: t < 500 ? "list" : t < 2800 ? "open" : "out" };
  if (t >= 3400) v.lines = [logLine("09:12:58", "No visit from Tom Reed.", "dim")];
  v.stamp = t >= 3300;
  return v;
};

const scenePriya = (t: number, c: Colleague): SceneView => {
  const v = baseView(stage(t, [
    [600, "Priya clicks the link in Alex's email."],
    [1500, "Your page opens for her."],
    [2800, "She looks at the form. She does not type anything."],
    [3600, "She closes the page."],
  ], c.detail));
  const onPage = t >= 450 && t < 3500;
  v.mode = onPage ? "live" : "idle";
  v.visitor = onPage ? c.name : null;
  v.cursor = t < 600 ? "off" : t < 1500 ? "page" : t < 2800 ? "email" : t < 3500 ? "close" : "off";
  v.click = within(t, 3300, 3480);
  v.visit = t >= 450;
  v.lines = [
    ...(t >= 500 ? [logLine("09:13:12", "Priya Shah opened your page.")] : []),
    ...(t >= 3500 ? [logLine("09:13:16", "Priya Shah left. Nothing typed.", "dim")] : []),
  ];
  v.stamp = t >= 3600;
  return v;
};

const sceneDan = (t: number, c: Colleague): SceneView => {
  const v = baseView(stage(t, [
    [600, "Dan clicks the link in Alex's email."],
    [1300, "Your page opens for him."],
    [1900, "He clicks the email box."],
    [3300, "He types his work email."],
    [3900, "He clicks the password box."],
    [4900, "He types his password."],
    [5700, "He clicks Confirm."],
    [6500, "Your page receives what he typed."],
  ], c.detail));
  v.mode = t < 450 ? "idle" : t < 5450 ? "live" : t < 6000 ? "sending" : "thanks";
  v.visitor = v.mode === "idle" ? null : c.name;
  v.cursor = t < 600 ? "off" : t < 1300 ? "page" : t < 3300 ? "email" : t < 4900 ? "pass" : "btn";
  v.click = within(t, 1750, 1930) || within(t, 3750, 3930) || within(t, 5400, 5580);
  v.focus = within(t, 1800, 3800) ? "email" : within(t, 3800, 5450) ? "pass" : null;
  v.email = typedPart(DAN_EMAIL, t, 1950, 48);
  v.pass = typedPart(DAN_PASS, t, 4000, 85);
  v.visit = t >= 450;
  v.lines = [
    ...(t >= 500 ? [logLine("09:14:02", "Dan Owens opened your page.")] : []),
    ...(t >= 5900 ? [logLine("09:14:09", `CAPTURED  ${DAN_EMAIL}  /  ${DAN_PASS}`, "bad")] : []),
  ];
  v.stamp = t >= 6500;
  return v;
};

const sceneLena = (t: number, c: Colleague): SceneView => {
  const v = baseView(stage(t, [
    [1300, "Lena reads Alex's email. She does not use the link."],
    [2600, "She opens the real HR site from her own bookmark."],
    [3700, "Your page gets no visit."],
  ], c.detail));
  if (t < 1300) v.overlay = { kind: "mail", who: c.name, step: "open" };
  else if (t < 3900) v.overlay = { kind: "web", who: c.name, step: t < 2300 ? "bookmark" : t < 3700 ? "real" : "out" };
  if (t >= 3800) v.lines = [logLine("09:13:41", "No visit from Lena Fox.", "dim")];
  v.stamp = t >= 3700;
  return v;
};

const sceneSam = (t: number, c: Colleague): SceneView => {
  const v = baseView(stage(t, [
    [900, "Sam reads Alex's email."],
    [1900, "The web address looks wrong to him."],
    [3000, "He reports the email to Security."],
  ], c.detail));
  if (t < 3500) {
    v.overlay = { kind: "mail", who: c.name, step: t < 900 ? "open" : t < 1900 ? "link" : t < 2600 ? "report" : t < 3300 ? "sent" : "out" };
  }
  if (t >= 3200) v.lines = [logLine("09:14:30", "No visit from Sam Ortiz.", "dim")];
  v.stamp = t >= 3000;
  return v;
};

const SCENES: Record<ColleagueId, (t: number, c: Colleague) => SceneView> = {
  tom: sceneTom, priya: scenePriya, dan: sceneDan, lena: sceneLena, sam: sceneSam,
};

/** A small colleague's mailbox laid over the attacker's page (Tom, Lena, Sam). */
function MiniMail({ who, step }: { who: string; step: MailStep }) {
  const reading = step !== "list";
  const reported = step === "sent" || step === "out";
  return (
    <div className={`bkr-win bkr-win--mail${step === "out" ? " is-out" : ""}`} role="img" aria-label={tx("{who}'s mailbox", { who })}>
      <div className="bkr-win-bar"><span>{txr("{who} — Outlook", { who })}</span><i aria-hidden="true"><b /><b /><b /></i></div>
      <div className="bkr-mail-tools" aria-hidden="true">
        <span>{tx("New mail")}</span>
        <span>{tx("Delete")}</span>
        <span className={`bkr-report${step === "report" ? " is-press" : ""}${reported ? " is-done" : ""}`}>
          <Ico name={reported ? "check" : "report"} />{reported ? tx("Reported to Security") : tx("Report")}
        </span>
      </div>
      {!reading ? (
        <div className="bkr-mail-row">
          <span className="bkr-mail-av">{tx("AM")}</span>
          <span className="bkr-mail-txt">
            <b>{tx("Alex Morgan")}</b>
            <span>{tx("Salary review — confirm your details to proceed")}</span>
            <small>{tx("Finance is finishing the salary review…")}</small>
          </span>
          <i className="bkr-dot" />
        </div>
      ) : (
        <div className="bkr-mail-read">
          <b>{tx("Salary review — confirm your details to proceed")}</b>
          <small>{tx("Alex Morgan <alex.morgan@asterbank.example> · 9:12 AM")}</small>
          <p>{tx("Hi team, Finance is finishing the salary review. Confirm your details through the link so your record is included.")}</p>
          <u className={`bkr-link${step === "link" ? " is-flag" : ""}`}>{SALARY_URL}</u>
        </div>
      )}
    </div>
  );
}

/** Lena's own browser: she skips the link and uses a bookmark to the real HR site. */
function MiniWeb({ who, step }: { who: string; step: WebStep }) {
  return (
    <div className={`bkr-win bkr-win--web${step === "out" ? " is-out" : ""}`} role="img" aria-label={tx("{who}'s browser", { who })}>
      <div className="bkr-win-bar"><span>{txr("{who} — Chrome", { who })}</span><i aria-hidden="true"><b /><b /><b /></i></div>
      <div className="bkr-web-url" aria-hidden="true"><Ico name="lock" /><span>{step === "bookmark" ? tx("New tab") : tx("hr.asterbank.example")}</span></div>
      <div className="bkr-web-marks" aria-hidden="true">
        <span className={`bkr-mark${step === "bookmark" ? " is-press" : ""}`}><Ico name="star" />{tx("HR portal")}</span>
        <span>{tx("Payroll")}</span>
        <span>{tx("Intranet")}</span>
      </div>
      <div className="bkr-web-page">
        {step === "bookmark" ? (
          <em>{tx("New tab")}</em>
        ) : (
          <>
            <b>{tx("Aster Bank · HR portal")}</b>
            <p>{tx("HR will contact you about the salary review. There is nothing to enter here.")}</p>
            <span className="bkr-real"><Ico name="check" />{tx("Real site")}</span>
          </>
        )}
      </div>
    </div>
  );
}

function ColleagueReel({ onDone }: { onDone: () => void }) {
  const [cur, setCur] = useState(-1);          // scene on screen (-1 = nothing played yet)
  const [t, setT] = useState(0);               // ms into that scene
  const [playing, setPlaying] = useState(false);
  const [run, setRun] = useState(0);           // bumps on every start, so a replay restarts the clock
  const [seen, setSeen] = useState<ColleagueId[]>([]);   // scenes that played to the end
  const [queued, setQueued] = useState<number | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const seenRef = useRef(seen);
  seenRef.current = seen;
  const queuedRef = useRef(queued);
  queuedRef.current = queued;
  const curRef = useRef(cur);
  curRef.current = cur;
  const gapRef = useRef<number | null>(null);
  const browserRef = useRef<HTMLDivElement | null>(null);
  const spots = useRef<Partial<Record<Exclude<CursorAt, "off">, HTMLElement | null>>>({});
  const spot = (k: Exclude<CursorAt, "off">) => (el: HTMLElement | null) => { spots.current[k] = el; };

  const play = (i: number) => {
    if (gapRef.current !== null) { window.clearTimeout(gapRef.current); gapRef.current = null; }
    setQueued(null);
    setCur(i);
    setT(0);
    setPlaying(true);
    setRun((r) => r + 1);
  };
  // After a scene: the person the learner clicked, else the next one nobody has watched yet.
  const playNext = () => {
    const q = queuedRef.current;
    if (q !== null) { play(q); return; }
    const i = COLLEAGUES.findIndex((p) => !seenRef.current.includes(p.id));
    if (i >= 0) play(i);
  };
  const choose = (i: number) => {
    if (playing) { if (i !== cur) setQueued(i); return; }   // a scene is never cut short
    play(i);
  };

  // The page sits live for a moment, then the first scene starts by itself.
  useEffect(() => {
    const id = window.setTimeout(() => { if (curRef.current < 0) playNext(); }, REEL_INTRO_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => { if (gapRef.current !== null) window.clearTimeout(gapRef.current); }, []);

  // One clock per scene. When it runs out the scene is marked as watched and the
  // next one starts after a short gap, unless the learner picked someone.
  useEffect(() => {
    if (!playing || cur < 0) return;
    const c = COLLEAGUES[cur];
    const t0 = performance.now();
    const iv = window.setInterval(() => {
      const el = performance.now() - t0;
      if (el < c.duration) { setT(el); return; }
      window.clearInterval(iv);
      setT(c.duration);
      setPlaying(false);
      setSeen((s) => (s.includes(c.id) ? s : [...s, c.id]));
      gapRef.current = window.setTimeout(() => { gapRef.current = null; playNext(); }, REEL_GAP_MS);
    }, 40);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  const c = cur >= 0 ? COLLEAGUES[cur] : null;
  const view: SceneView = c
    ? SCENES[c.id](t, c)
    : baseView("Alex's email just went out to five colleagues. You are watching your own page. Watch what each one does.");
  const done = seen.length === COLLEAGUES.length && !playing;

  // What the site has recorded: earlier scenes in the order they finished, then this one so far.
  const before = seen.filter((id) => id !== c?.id);
  const history = before.flatMap((id) => {
    const p = COLLEAGUES.find((x) => x.id === id)!;
    return SCENES[id](p.duration, p).lines;
  });
  const lines = [...history, ...view.lines];
  const visits = before.filter((id) => id === "priya" || id === "dan").length + (view.visit ? 1 : 0);
  const passwords = (before.includes("dan") ? 1 : 0) + (view.lines.some((l) => l.tone === "bad") ? 1 : 0);

  // The pretend mouse follows the real form: measure where each target sits.
  useLayoutEffect(() => {
    const b = browserRef.current;
    if (!b) return;
    const br = b.getBoundingClientRect();
    if (view.cursor === "off") { setCursorPos({ x: br.width + 24, y: br.height * 0.6 }); return; }
    const el = spots.current[view.cursor];
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCursorPos({ x: r.left - br.left + r.width * 0.7, y: r.top - br.top + r.height * 0.55 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.cursor, run]);

  return (
    <div className="bk-app bkr">
      <div className="bk-app-bar"><span className="bk-app-title">{tx("Your website — you are watching who signs in")}</span></div>
      <div className="bk-app-body">
        <section className="bkr-side" aria-label={tx("Colleagues")}>
          <p className="bkr-side-head">{tx("Colleagues who got Alex's email")}</p>
          <p className="bkr-side-sub">{tx("Each scene plays by itself. Click a name to watch that person next.")}</p>
          <ul className="bkr-people">
            {COLLEAGUES.map((p, i) => {
              const isSeen = seen.includes(p.id);
              const isNow = playing && cur === i;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`bkr-person${isNow ? " is-now" : ""}${isSeen ? " is-seen" : ""}`}
                    aria-current={isNow ? "true" : undefined}
                    onClick={() => choose(i)}
                  >
                    <span className="bkr-av">{p.initials}</span>
                    <span className="bkr-who"><strong>{p.name}</strong><span>{p.role}</span></span>
                    {isNow ? <span className="bkr-tag bkr-tag--now">{tx("Watching")}</span>
                      : queued === i ? <span className="bkr-tag bkr-tag--next">{tx("Up next")}</span>
                      : isSeen ? <span className={`bkr-tag bkr-tag--${p.tone}`}>{p.chip}</span>
                      : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="bkr-side-foot">
            {done ? (
              <>
                <p className="bkr-sum">{txr("<0>Five colleagues, one password.</0> Only typing it into your page counted.", {}, [<strong />])}</p>
                <button type="button" className="bk-btn bk-btn--primary" onClick={onDone}>{tx("Continue")}</button>
              </>
            ) : (
              <p className="bkr-count">{txr("{length} of {length2} watched", { length: seen.length, length2: COLLEAGUES.length })}</p>
            )}
          </div>
        </section>

        <section className="bkr-stage" aria-label={tx("Your salary-review site")}>
          <header className="bkr-head">
            <span className="bkr-scene">{c ? tx("Scene {v} of {length}", { v: cur + 1, length: COLLEAGUES.length }) : tx("Your page is live")}</span>
            <strong>{c ? `${c.name} · ${c.role}` : tx("Waiting for the first colleague")}</strong>
            <span className="bkr-bar" aria-hidden="true"><i style={{ width: `${c ? Math.min(100, (t / c.duration) * 100) : 0}%` }} /></span>
          </header>

          <div className={`bkr-browser${view.overlay ? " has-overlay" : ""}`} ref={browserRef}>
            <div className="bkr-tabs" aria-hidden="true">
              <span className="bkr-tab">{txr("<0>A</0>Salary review 2026", {}, [<i className="bkr-fav" />])}<b ref={spot("close")}>×</b></span>
            </div>
            <div className="bkr-url">
              <Ico name="lock" />
              <span className="bkr-url-text">{SALARY_URL}</span>
              <em className={view.visitor ? "is-on" : ""}>{view.visitor ? tx("Visitor: {visitor}", { visitor: view.visitor }) : tx("No visitor")}</em>
            </div>
            <div className={`bkr-page is-${view.mode}`}>
              <div className="bkr-site-head"><i className="bkr-logo">{tx("A")}</i><b>{tx("Aster Bank")}</b></div>
              <div className="bkr-body">
              {view.mode === "thanks" ? (
                <div className="bkr-card bkr-card--thanks">
                  <h3>{tx("Thank you")}</h3>
                  <p>{tx("Your details were received. HR will be in touch.")}</p>
                  <i className="bkr-tick"><Ico name="check" /></i>
                </div>
              ) : (
                <div className="bkr-card">
                  <h3 ref={spot("page")}>{tx("Confirm your salary details")}</h3>
                  <p>{tx("Sign in with your work account so your record is included in this year's review.")}</p>
                  <div className="bkr-row">
                    <span>{tx("Work email")}</span>
                    <div className={`bkr-input${view.focus === "email" ? " is-focus" : ""}`} ref={spot("email")}>
                      <span className="bkr-typed bkr-typed--email">{view.email}</span>
                      {view.focus === "email" && <i className="bkr-caret" />}
                    </div>
                  </div>
                  <div className="bkr-row">
                    <span>{tx("Password")}</span>
                    <div className={`bkr-input${view.focus === "pass" ? " is-focus" : ""}`} ref={spot("pass")}>
                      <span className="bkr-typed bkr-typed--pass">{view.pass}</span>
                      {view.focus === "pass" && <i className="bkr-caret" />}
                    </div>
                  </div>
                  <div className={`bkr-btn${view.mode === "sending" ? " is-busy" : ""}`} ref={spot("btn")}>
                    {view.mode === "sending" ? <><i className="bkg-spin" />{tx("Sending")}</> : tx("Confirm details")}
                  </div>
                </div>
              )}
              </div>
            </div>
            <span
              className={`bkr-cursor${view.cursor === "off" ? " is-off" : ""}${view.click ? " is-click" : ""}`}
              style={{ transform: `translate(${cursorPos.x}px, ${cursorPos.y}px)` }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 20 20"><path d="M3 2l12 8.2-5.2.9 3 5.6-2.2 1.2-3-5.6L3.9 16z" fill="#fff" stroke="#111" strokeWidth="1.1" strokeLinejoin="round" /></svg>
            </span>
            {view.overlay?.kind === "mail" && <MiniMail who={view.overlay.who} step={view.overlay.step} />}
            {view.overlay?.kind === "web" && <MiniWeb who={view.overlay.who} step={view.overlay.step} />}
            {view.stamp && c && <span className={`bkr-stamp ${c.tone}`}>{c.outcome}</span>}
          </div>

          <p className="bkr-caption" role="status" aria-live="polite">{view.caption}</p>

          <div className="bkr-log" aria-label={tx("What your site recorded")}>
            <div className="bkr-log-head">
              <span>{tx("Your site log")}</span>
              <span>{txr("visits {visits} · passwords {passwords}", { visits, passwords })}</span>
            </div>
            <ul>
              {lines.slice(-3).map((l, i) => (
                <li key={`${l.at}-${i}`} className={`bkr-log-${l.tone}`}><span>{l.at}</span>{l.text}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}

const PW_ITEMS = [
  { id: "p1", value: "password1", note: "A very common password", answer: "easy", line: "Easy to guess. It is on every common-password list." },
  { id: "p2", value: "Summer2025", note: "A season plus a year", answer: "easy", line: "Predictable. Season plus year is a known pattern." },
  { id: "p3", value: "Chen2004", note: "A family name and a birth year", answer: "easy", line: "Predictable once those personal facts are public." },
  { id: "p4", value: "7bQ!v29sLxKd0p", note: "Dan's long password. He typed it into your fake page.", answer: "hard", line: "Hard to guess. You still have it, because he typed it into your page." },
];

function CollectedPasswords({ onDone }: { onDone: () => void }) {
  const [idx, setIdx] = useState(0);
  const [pick, setPick] = useState<string | null>(null);
  const item = PW_ITEMS[idx];
  const advance = () => {
    if (idx < PW_ITEMS.length - 1) { setIdx(idx + 1); setPick(null); return; }
    onDone();
  };
  return (
    <div className="bk-app">
      <div className="bk-app-bar"><span className="bk-app-title">{tx("Passwords you collected on the way in")}</span></div>
      <div className="bk-app-body">
        <p>{txr("Password {v} of {length}. Would this have been hard to guess if you had never seen it?", { v: idx + 1, length: PW_ITEMS.length })}</p>
        <h2><code>{item.value}</code></h2>
        <p>{item.note}</p>
        <div className="bk-qopts">
          <button type="button" className="bk-qopt" disabled={!!pick} onClick={() => setPick("easy")}>{tx("Easy or predictable")}</button>
          <button type="button" className="bk-qopt" disabled={!!pick} onClick={() => setPick("hard")}>{tx("Hard to guess")}</button>
        </div>
        {pick && (
          <>
            <p>{item.line}</p>
            <button type="button" className="bk-btn bk-btn--primary" onClick={advance}>
              {idx < PW_ITEMS.length - 1 ? tx("Next password") : tx("Continue")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Why the attacker opens Maya's Facebook page                                */
/* ------------------------------------------------------------------------- */

const WHY_POINTS = [
  { id: "access", head: "Maya can sign in to the payment system.", body: "That is the bank system you are trying to get into." },
  { id: "nopass", head: "You do not have her password.", body: "Nothing in Alex's mailbox gives it to you." },
  { id: "profile", head: "Her Facebook page is public.", body: "You will open it and collect facts that could help you guess her password: a pet, a birthday, a team. The guesser tries those next." },
];
/** The three points arrive one after another; Continue unlocks once the last one is on screen. */
const WHY_READY_MS = 3600;

function WhyFacebook({ onContinue }: { onContinue: () => void }) {
  const [ready, setReady] = useState(() => prefersReducedMotion());
  useEffect(() => {
    if (ready) return;
    const id = window.setTimeout(() => setReady(true), WHY_READY_MS);
    return () => window.clearTimeout(id);
  }, [ready]);

  return (
    <div className="bk-app bkw">
      <div className="bk-app-bar">
        <span className="bk-app-dot" />
        <span className="bk-app-title">{tx("Outlook — Alex Morgan's mailbox")}</span>
      </div>
      <div className="bk-app-body">
        <section className="bkw-mail" aria-label={tx("A message from Maya Chen")}>
          <p className="bkw-kick">{tx("You found this in Alex's mailbox")}</p>
          <article className="bkw-msg">
            <h2>{tx("Re: Q2 payment batch")}</h2>
            <div className="bkw-meta">
              <span className="bkw-av"><FaceArt /></span>
              <span className="bkw-meta-txt">
                <b>{tx("Maya Chen")}</b> <span>{tx("<maya.chen@asterbank.example>")}</span>
                <small>{tx("To: Alex Morgan · 8:52 AM")}</small>
              </span>
            </div>
            <p>{tx("Hi Alex,")}</p>
            <p>{txr("I <0>approved the Q2 batch in the payment system</0> this morning.", {}, [<mark />])}</p>
            <p>{txr("Maya{br}Payments, Finance", { br: <br /> })}</p>
          </article>
          <p className="bkw-note">{tx("You only sent the salary draft earlier. This message was sitting in Alex's inbox the whole time.")}</p>
        </section>

        <section className="bkw-story" aria-label={tx("Why this matters")}>
          <p className="bkw-kick">{tx("So why open her Facebook page?")}</p>
          <ol className="bkw-points">
            {WHY_POINTS.map((p, i) => (
              <li key={p.id}>
                <span className="bkw-n">{i + 1}</span>
                <span className="bkw-txt"><strong>{p.head}</strong> {p.body}</span>
              </li>
            ))}
          </ol>
          <button type="button" className="bk-btn bk-btn--primary bkw-go" disabled={!ready} onClick={onContinue}>
            {tx("Continue")}
          </button>
        </section>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Maya's Facebook profile                                                    */
/* ------------------------------------------------------------------------- */

type IcoName =
  | "search" | "home" | "people" | "watch" | "shop" | "grid" | "chat" | "bell"
  | "like" | "heart" | "comment" | "share" | "globe" | "paw" | "cake" | "ball" | "scan" | "check" | "lock"
  | "report" | "star";

const STROKE = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const;

const ICO_PATHS: Record<IcoName, ReactNode> = {
  search: <path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />,
  home: <path d="M12 2.8 2.6 11.2h2.7V20h5.2v-5.6h3V20h5.2v-8.8h2.7z" />,
  people: <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />,
  watch: <><rect x="3" y="5" width="18" height="14" rx="3.5" {...STROKE} /><path d="M10.2 9.3v5.4l4.6-2.7z" /></>,
  shop: <><path d="M4 9.5 5.3 4.5h13.4L20 9.5" {...STROKE} /><path d="M4 9.5a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0" {...STROKE} /><path d="M5.5 12.6V20h13v-7.4" {...STROKE} /></>,
  grid: <>{[6, 12, 18].map((y) => [6, 12, 18].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.8" />))}</>,
  chat: <path d="M12 3C6.9 3 3 6.7 3 11.4c0 2.6 1.2 4.9 3.1 6.4V21l3-1.7c.9.3 1.9.4 2.9.4 5.1 0 9-3.7 9-8.4S17.1 3 12 3z" />,
  bell: <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />,
  like: <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />,
  heart: <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />,
  comment: <path d="M4 4.5h16v11H9l-5 4.5z" {...STROKE} />,
  share: <path d="M14 9V5l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" />,
  globe: <><circle cx="12" cy="12" r="9" {...STROKE} /><ellipse cx="12" cy="12" rx="4" ry="9" {...STROKE} /><path d="M3 12h18" {...STROKE} /></>,
  paw: <><ellipse cx="5.6" cy="11.2" rx="2" ry="2.7" /><ellipse cx="9.6" cy="6.6" rx="2.1" ry="2.8" /><ellipse cx="14.4" cy="6.6" rx="2.1" ry="2.8" /><ellipse cx="18.4" cy="11.2" rx="2" ry="2.7" /><path d="M12 11.2c-3 0-5.6 3.2-5.6 5.6 0 1.8 1.4 2.6 2.8 2.6 1 0 1.8-.5 2.8-.5s1.8.5 2.8.5c1.4 0 2.8-.8 2.8-2.6 0-2.4-2.6-5.6-5.6-5.6z" /></>,
  cake: <path d="M12 6c1.11 0 2-.9 2-2 0-.38-.1-.73-.29-1.03L12 0l-1.71 2.97c-.19.3-.29.65-.29 1.03 0 1.1.9 2 2 2zm4.6 9.99l-1.07-1.07-1.08 1.07c-1.3 1.3-3.58 1.31-4.89 0l-1.07-1.07-1.09 1.07C6.75 16.64 5.88 17 4.96 17c-.73 0-1.4-.23-1.96-.61V21c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-4.61c-.56.38-1.23.61-1.96.61-.92 0-1.79-.36-2.44-1.01zM18 9h-5V7h-2v2H6c-1.66 0-3 1.34-3 3v1.54c0 1.08.88 1.96 1.96 1.96.52 0 1.02-.2 1.38-.57l2.14-2.13 2.13 2.13c.74.74 2.03.74 2.77 0l2.14-2.13 2.13 2.13c.37.37.86.57 1.38.57 1.08 0 1.96-.88 1.96-1.96V12C21 10.34 19.66 9 18 9z" />,
  ball: <><circle cx="12" cy="12" r="9" {...STROKE} /><path d="M12 3v18M3 12h18" {...STROKE} /><path d="M5.7 5.7c3.2 2.7 3.2 10.9 0 12.6M18.3 5.7c-3.2 2.7-3.2 10.9 0 12.6" {...STROKE} /></>,
  scan: <><path d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4" {...STROKE} /><circle cx="12" cy="12" r="2.4" /></>,
  check: <path d="M5 12.5 10 17.5 19 7" {...STROKE} strokeWidth={3} />,
  lock: <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />,
  report: <path d="M15.73 3H8.27L3 8.27v7.46L8.27 21h7.46L21 15.73V8.27L15.73 3zM12 17.3c-.72 0-1.3-.58-1.3-1.3 0-.72.58-1.3 1.3-1.3.72 0 1.3.58 1.3 1.3 0 .72-.58 1.3-1.3 1.3zm1-4.3h-2V7h2v6z" />,
  star: <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6L2.5 9.4l6.6-.8z" />,
};

function Ico({ name }: { name: IcoName }) {
  return (
    <svg className="bkfb-ico" viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false">
      {ICO_PATHS[name]}
    </svg>
  );
}

/** Deterministic row of rectangles used for the cover's skyline. */
function skyline(seed: number, minH: number, maxH: number, minW: number, maxW: number) {
  let s = seed;
  const rnd = () => { s = (s * 1664525 + 1013904223) % 4294967296; return s / 4294967296; };
  const out: Array<{ x: number; w: number; h: number }> = [];
  let x = -12;
  while (x < 1012) {
    const w = minW + rnd() * (maxW - minW);
    const h = minH + rnd() * (maxH - minH);
    out.push({ x, w, h });
    x += w + rnd() * 3;
  }
  return out;
}
const SKY_FAR = skyline(7, 26, 62, 26, 52);
const SKY_NEAR = skyline(23, 16, 44, 30, 62);

/** Cover picture: a park below a city skyline at sunset. Pure SVG, no image file. */
function CoverArt() {
  return (
    <svg viewBox="0 0 1000 150" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="bkfbSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5eaef0" />
          <stop offset=".5" stopColor="#b9def9" />
          <stop offset=".82" stopColor="#ffe3bf" />
          <stop offset="1" stopColor="#ffd09a" />
        </linearGradient>
        <radialGradient id="bkfbSun" cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor="#fffbe2" />
          <stop offset=".4" stopColor="#ffe9a6" stopOpacity=".92" />
          <stop offset="1" stopColor="#ffe9a6" stopOpacity="0" />
        </radialGradient>
        <pattern id="bkfbWin" width="8" height="10" patternUnits="userSpaceOnUse">
          <rect x="2" y="2" width="3.4" height="4.6" fill="#ffefb8" opacity=".8" />
        </pattern>
      </defs>
      <rect width="1000" height="150" fill="url(#bkfbSky)" />
      <circle cx="770" cy="76" r="58" fill="url(#bkfbSun)" />
      <circle cx="770" cy="76" r="15" fill="#fff7cf" />
      <g fill="#fff" opacity=".88">
        <ellipse cx="170" cy="34" rx="62" ry="10" /><ellipse cx="214" cy="27" rx="38" ry="9" /><ellipse cx="128" cy="30" rx="32" ry="7" />
        <ellipse cx="470" cy="22" rx="52" ry="8" /><ellipse cx="508" cy="17" rx="30" ry="7" />
        <ellipse cx="900" cy="38" rx="46" ry="8" /><ellipse cx="934" cy="32" rx="26" ry="6" />
      </g>
      <g fill="none" stroke="#51708f" strokeWidth="1.6" strokeLinecap="round">
        <path d="M330 30q6-6 12 0q6-6 12 0" /><path d="M372 42q5-5 10 0q5-5 10 0" />
      </g>
      <g fill="#aac6e2">
        {SKY_FAR.map((b, i) => <rect key={i} x={b.x} y={118 - b.h} width={b.w} height={b.h} />)}
      </g>
      <g fill="#7b9dc4">
        {SKY_NEAR.map((b, i) => <rect key={i} x={b.x} y={124 - b.h} width={b.w} height={b.h} />)}
      </g>
      <g fill="url(#bkfbWin)" opacity=".7">
        {SKY_NEAR.map((b, i) => <rect key={i} x={b.x} y={124 - b.h} width={b.w} height={b.h} />)}
      </g>
      <path d="M0 114C160 99 330 99 500 113S820 130 1000 107L1000 150L0 150Z" fill="#86d28d" />
      <path d="M0 130C220 111 400 116 600 128S880 138 1000 122L1000 150L0 150Z" fill="#5fb86e" />
      <path d="M0 144C260 134 560 138 1000 140L1000 150L0 150Z" fill="#46a05a" />
      <path d="M560 150C575 136 620 128 700 126L712 130C640 134 604 142 594 150Z" fill="#f2e6c6" opacity=".9" />
      <g>
        <rect x="156" y="104" width="4" height="20" fill="#6b4a2f" />
        <circle cx="158" cy="96" r="14" fill="#3f9d57" /><circle cx="149" cy="102" r="9" fill="#4bae63" /><circle cx="168" cy="102" r="10" fill="#43a35c" />
        <rect x="846" y="108" width="4" height="18" fill="#6b4a2f" />
        <circle cx="848" cy="100" r="12" fill="#3f9d57" /><circle cx="840" cy="106" r="8" fill="#4bae63" /><circle cx="857" cy="106" r="9" fill="#43a35c" />
      </g>
    </svg>
  );
}

/** Maya's profile picture: a drawn face, not a photo and not initials. */
function FaceArt() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      <rect width="100" height="100" fill="#a9dbe9" />
      <circle cx="50" cy="104" r="62" fill="#c4e8f2" />
      <path d="M25 52C21 27 36 14 52 14c17 0 29 14 23 40 2 12 0 21-5 26H30c-4-6-6-16-5-28z" fill="#231a1d" />
      <path d="M8 100c2-19 20-26 42-26s40 7 42 26z" fill="#2c4068" />
      <path d="M39 75l11 17 11-17z" fill="#f4f6fa" />
      <rect x="43" y="62" width="14" height="16" rx="5" fill="#e8b88f" />
      <ellipse cx="50" cy="48" rx="19" ry="22" fill="#f6d2b0" />
      <path d="M29.5 45C29 26 43 21 52 21c11 0 21 8 18.5 24C62 37 52 34 41 37c-5 1.5-9 4-11.5 8z" fill="#231a1d" />
      <ellipse cx="42" cy="50" rx="2.2" ry="2.8" fill="#2a2024" />
      <ellipse cx="58" cy="50" rx="2.2" ry="2.8" fill="#2a2024" />
      <circle cx="42.8" cy="49" r=".8" fill="#fff" />
      <circle cx="58.8" cy="49" r=".8" fill="#fff" />
      <path d="M37.5 44.5Q42 42 46.5 44.5M53.5 44.5Q58 42 62.5 44.5" fill="none" stroke="#231a1d" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="36.5" cy="57" r="4" fill="#f09a8c" opacity=".38" />
      <circle cx="63.5" cy="57" r="4" fill="#f09a8c" opacity=".38" />
      <path d="M50 52Q48.5 56 50.5 57" fill="none" stroke="#d99a7a" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M43.5 60.5Q50 66 56.5 60.5" fill="none" stroke="#b5483f" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/** The post photo: Biscuit, a golden retriever, in a park. Pure SVG. */
function DogPhoto() {
  return (
    <svg viewBox="0 0 480 240" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="bkfbDogSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#80c3f2" />
          <stop offset="1" stopColor="#e1f2ff" />
        </linearGradient>
        <linearGradient id="bkfbDogGrass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#85d57e" />
          <stop offset="1" stopColor="#3e984c" />
        </linearGradient>
      </defs>
      <rect width="480" height="240" fill="url(#bkfbDogSky)" />
      <circle cx="64" cy="48" r="26" fill="#fff4b8" opacity=".92" />
      <g fill="#fff" opacity=".92"><ellipse cx="320" cy="44" rx="46" ry="11" /><ellipse cx="354" cy="36" rx="28" ry="9" /></g>
      <g fill="#3d8d54">
        <circle cx="34" cy="122" r="30" /><circle cx="86" cy="114" r="36" /><circle cx="150" cy="124" r="28" />
        <circle cx="402" cy="116" r="34" /><circle cx="454" cy="126" r="30" />
      </g>
      <rect y="124" width="480" height="116" fill="url(#bkfbDogGrass)" />
      <ellipse cx="148" cy="212" rx="54" ry="12" fill="#7d6444" opacity=".88" />
      <ellipse cx="146" cy="210" rx="40" ry="8" fill="#a98c66" opacity=".75" />
      <circle cx="402" cy="214" r="9" fill="#d5e84a" /><path d="M394.5 211Q402 217 409.5 211" fill="none" stroke="#fff" strokeWidth="1.6" />
      <path d="M352 190C384 178 398 152 386 134C380 150 368 160 346 172Z" fill="#c68a36" />
      <ellipse cx="300" cy="168" rx="58" ry="44" fill="#d9a04a" />
      <ellipse cx="300" cy="178" rx="30" ry="32" fill="#f0c97e" />
      <rect x="270" y="182" width="22" height="34" rx="10" fill="#e1a94f" />
      <rect x="308" y="182" width="22" height="34" rx="10" fill="#e1a94f" />
      <ellipse cx="281" cy="214" rx="16" ry="7" fill="#7a5a3a" />
      <ellipse cx="319" cy="214" rx="16" ry="7" fill="#7a5a3a" />
      <ellipse cx="258" cy="104" rx="16" ry="30" fill="#b9792b" transform="rotate(14 258 104)" />
      <ellipse cx="342" cy="104" rx="16" ry="30" fill="#b9792b" transform="rotate(-14 342 104)" />
      <circle cx="300" cy="98" r="40" fill="#e7b257" />
      <ellipse cx="300" cy="114" rx="24" ry="18" fill="#f4d596" />
      <ellipse cx="300" cy="106" rx="8" ry="6" fill="#2a1f1c" />
      <circle cx="285" cy="90" r="4.2" fill="#2a1f1c" /><circle cx="315" cy="90" r="4.2" fill="#2a1f1c" />
      <circle cx="286.4" cy="88.6" r="1.4" fill="#fff" /><circle cx="316.4" cy="88.6" r="1.4" fill="#fff" />
      <path d="M300 112V119M300 119Q291 128 283 120M300 119Q309 128 317 120" fill="none" stroke="#2a1f1c" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M294 124Q300 142 306 124Z" fill="#f08aa0" />
      <path d="M268 138Q300 156 332 138" fill="none" stroke="#d9473f" strokeWidth="7" strokeLinecap="round" />
      <circle cx="300" cy="152" r="6" fill="#f5d36a" stroke="#c99a1d" strokeWidth="1.5" />
    </svg>
  );
}

const FB_ICON: Record<string, IcoName> = { pet: "paw", bday: "cake", team: "ball" };
/** First fact is picked up this long after the page opens, then one per second. */
const FB_START_MS = 900;
const FB_STEP_MS = 1000;
/** Time the tool spends "copying" a row before the note appears. */
const FB_COPY_MS = 520;

function prefersReducedMotion() {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function FacebookProfile({ taken, onTake, onDone }: { taken: string[]; onTake: (text: string) => void; onDone: () => void }) {
  const pageRef = useRef<HTMLDivElement | null>(null);
  const colsRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Array<HTMLLIElement | null>>([]);
  const takeRef = useRef(onTake);
  takeRef.current = onTake;
  const takenRef = useRef(taken);
  takenRef.current = taken;
  const [reading, setReading] = useState(-1);

  useEffect(() => {
    const timers: number[] = [];
    const later = (ms: number, fn: () => void) => { timers.push(window.setTimeout(fn, ms)); };
    const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";

    // The attacker scrolls down the profile to the Intro card, just under the 46px top bar.
    later(450, () => {
      const page = pageRef.current;
      const cols = colsRef.current;
      if (!page || !cols) return;
      const top = cols.getBoundingClientRect().top - page.getBoundingClientRect().top + page.scrollTop - 42;
      if (top > page.scrollTop + 8) page.scrollTo({ top, behavior });
    });

    // A small monitor cannot show the whole Intro card: keep the row being read between the top bar and the tool bar.
    const keepInView = (el: HTMLElement | null) => {
      const page = pageRef.current;
      if (!page || !el) return;
      const pr = page.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      const topLimit = pr.top + 46 + 14;
      const bottomLimit = pr.bottom - (barRef.current?.offsetHeight ?? 54) - 12;
      const delta = er.bottom > bottomLimit ? er.bottom - bottomLimit : er.top < topLimit ? er.top - topLimit : 0;
      if (Math.abs(delta) > 2) page.scrollTo({ top: page.scrollTop + delta, behavior });
    };

    // Facts are read and copied one after another, about one second apart.
    CLUES.forEach((c, i) => {
      const text = `${c.label}: ${c.value}`;
      if (takenRef.current.includes(text)) return;
      later(FB_START_MS + i * FB_STEP_MS, () => { setReading(i); keepInView(rowRefs.current[i]); });
      later(FB_START_MS + i * FB_STEP_MS + FB_COPY_MS, () => takeRef.current(text));
    });
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);

  const copiedOf = (label: string) => taken.some((n) => n.startsWith(`${label}:`));
  const copied = CLUES.filter((c) => copiedOf(c.label)).length;
  const finished = copied >= CLUES.length;

  return (
    <div className="browser winapp bkfbwin">
      <WindowTitlebar title={tx("Chrome")} />
      <div className="chrome">
        <div className="nav">
          <span>{"\u2190"}</span>
          <span>{"\u2192"}</span>
          <span>{"\u21BB"}</span>
        </div>
        <div className="urlbar">
          <span className="lock">{"\u{1F512}"}</span>
          <input value="https://www.facebook.com/maya.chen" readOnly aria-label={tx("Address bar")} />
        </div>
      </div>
      <div className="page bkfb-page" ref={pageRef}>
        <header className="bkfb-nav">
          <span className="bkfb-logo">{tx("facebook")}</span>
          <div className="bkfb-search" aria-hidden="true"><Ico name="search" /><span>{tx("Search Facebook")}</span></div>
          <nav className="bkfb-navc" aria-hidden="true">
            <span className="is-on"><Ico name="home" /></span>
            <span><Ico name="people" /></span>
            <span><Ico name="watch" /></span>
            <span><Ico name="shop" /></span>
          </nav>
          <div className="bkfb-navr" aria-hidden="true">
            <span><Ico name="grid" /></span>
            <span><Ico name="chat" /></span>
            <span><Ico name="bell" /></span>
            <span className="bkfb-me" />
          </div>
        </header>

        <div className="bkfb-wrap">
          <section className="bkfb-card bkfb-profile" aria-label={tx("Maya Chen's profile")}>
            <div className="bkfb-cover"><CoverArt /></div>
            <div className="bkfb-head">
              <div className="bkfb-ava"><FaceArt /></div>
              <div className="bkfb-who">
                <h2 className="bkfb-name">{tx("Maya Chen")}</h2>
                <p className="bkfb-sub"><strong>612</strong> {txr("friends · Works at <0>Aster Bank</0>", {}, [<strong />])}</p>
                <div className="bkfb-frs" aria-hidden="true">
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => <i key={i} className={`bkfb-fr bkfb-fr--${i}`} />)}
                </div>
              </div>
              <div className="bkfb-btns" aria-hidden="true">
                <span className="bkfb-btn"><Ico name="people" />{tx("Friends")}</span>
                <span className="bkfb-btn bkfb-btn--blue"><Ico name="chat" />{tx("Message")}</span>
              </div>
            </div>
            <div className="bkfb-tabs" aria-hidden="true">
              <span className="is-on">{tx("Posts")}</span>
              <span>{tx("About")}</span>
              <span>{tx("Photos")}</span>
            </div>
          </section>

          <div className="bkfb-cols" ref={colsRef}>
            <aside className="bkfb-left">
              <section className="bkfb-card bkfb-intro" aria-label={tx("Intro")}>
                <div className="bkfb-intro-top">
                  <h3>{tx("Intro")}</h3>
                  <span className="bkfb-public"><Ico name="globe" />{tx("Public")}</span>
                </div>
                <ul className="bkfb-facts">
                  {CLUES.map((c, i) => {
                    const done = copiedOf(c.label);
                    const busy = !done && reading === i;
                    return (
                      <li
                        key={c.id}
                        ref={(el) => { rowRefs.current[i] = el; }}
                        className={`bkfb-fact${done ? " is-copied" : ""}${busy ? " is-reading" : ""}`}
                      >
                        <span className="bkfb-fact-ico"><Ico name={FB_ICON[c.id]} /></span>
                        <span className="bkfb-fact-txt"><span className="bkfb-fact-k">{c.label}</span><strong>{c.value}</strong></span>
                        {done && <em className="bkfb-fact-chip bkfb-fact-chip--done"><Ico name="check" />{tx("Copied to notes")}</em>}
                        {busy && <em className="bkfb-fact-chip bkfb-fact-chip--busy">{tx("Copying")}</em>}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </aside>

            <article className="bkfb-card bkfb-post" aria-label={tx("A public post")}>
              <div className="bkfb-post-head">
                <span className="bkfb-post-ava"><FaceArt /></span>
                <div>
                  <p className="bkfb-post-name">{tx("Maya Chen")}</p>
                  <p className="bkfb-post-meta">{tx("2 d ·")} <Ico name="globe" /> {tx("Public")}</p>
                </div>
              </div>
              <p className="bkfb-post-text">{tx("Biscuit found the one muddy puddle in the whole park. Again. Worth it.")}</p>
              <div className="bkfb-photo"><DogPhoto /></div>
              <div className="bkfb-stats" aria-hidden="true">
                <span className="bkfb-react">
                  <span className="bkfb-react-faces">
                    <i className="bkfb-react-like"><Ico name="like" /></i>
                    <i className="bkfb-react-love"><Ico name="heart" /></i>
                  </span>
                  48
                </span>
                <span>{tx("6 comments · 2 shares")}</span>
              </div>
              <div className="bkfb-post-actions" aria-hidden="true">
                <span><Ico name="like" />{tx("Like")}</span>
                <span><Ico name="comment" />{tx("Comment")}</span>
                <span><Ico name="share" />{tx("Share")}</span>
              </div>
            </article>
          </div>
        </div>

        <div className="bkfb-tool" ref={barRef} role="status" aria-live="polite">
          <span className="bkfb-tool-ico" aria-hidden="true"><Ico name="scan" /></span>
          <p className="bkfb-tool-txt">
            <strong>{finished ? tx("All 3 facts are on your notes.") : tx("Your tool is copying public profile data.")}</strong>{" "}
            {finished
              ? tx("Nothing here needed a password. Anyone can read this page.")
              : tx("Anyone can open this page, so there is nothing for you to click.")}
          </p>
          <span className="bkfb-tool-count">{txr("{copied} of {length}", { copied, length: CLUES.length })}</span>
          <span className="bkfb-tool-pips" aria-hidden="true">
            {CLUES.map((c) => <i key={c.id} className={copiedOf(c.label) ? "on" : ""} />)}
          </span>
          <button type="button" className="bk-btn bk-btn--primary" disabled={!finished} onClick={onDone}>
            {tx("Back to the desktop")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* The guessing tool                                                          */
/* ------------------------------------------------------------------------- */

/** Peak gain for every sound in the tool. Quiet on purpose. */
const SFX_GAIN = 0.04;

type Sfx = { key: () => void; enter: () => void; deny: () => void; win: () => void; close: () => void };

/**
 * Real WebAudio for the tool: a short square-wave tick per typed character, a
 * low sawtooth thud when the bank denies a guess, a three-note sine rise when
 * it accepts. Created inside the Run click so the browser allows sound.
 */
function createSfx(): Sfx | null {
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  let ctx: AudioContext;
  try { ctx = new AC(); } catch { return null; }
  void ctx.resume().catch(() => {});

  const blip = (type: OscillatorType, f0: number, f1: number, dur: number, peak: number, at = 0, lowpass = 0) => {
    if (!audio.isEnabled()) return;
    const t0 = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t0);
    if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
    amp.gain.setValueAtTime(0.0001, t0);
    amp.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.006, dur / 3));
    amp.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    if (lowpass) {
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = lowpass;
      osc.connect(lp);
      lp.connect(amp);
    } else {
      osc.connect(amp);
    }
    amp.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.03);
  };

  return {
    key: () => {
      const f = 1500 + Math.random() * 700;
      blip("square", f, f * 0.55, 0.03, SFX_GAIN, 0, 4200);
    },
    enter: () => blip("square", 640, 340, 0.07, SFX_GAIN, 0, 3000),
    deny: () => {
      blip("sawtooth", 170, 55, 0.3, SFX_GAIN * 1.25, 0, 900);
      blip("sawtooth", 110, 48, 0.22, SFX_GAIN, 0.11, 700);
    },
    win: () => {
      [523.25, 659.25, 783.99].forEach((f, i) => blip("sine", f, f, i === 2 ? 0.42 : 0.2, SFX_GAIN, i * 0.14));
    },
    close: () => { void ctx.close().catch(() => {}); },
  };
}

type GuessPhase = "idle" | "typing" | "checking" | "result" | "clearing" | "won";
type Attempt = { pw: string; ok: boolean; src: string };

const GUESS_FIRST_KEY_MS = 300;   // pause before the first key of each guess
const GUESS_KEY_MS = 55;          // between keys (plus up to 18 ms of jitter, so 55-73 ms; the scene adds a few ms)
const GUESS_SUBMIT_MS = 170;      // last key to "Sign in"
const GUESS_CHECK_MS = 560;       // the bank is checking
const GUESS_DENY_HOLD_MS = 900;   // DENIED stays on screen
const GUESS_WIN_HOLD_MS = 1500;   // ACCEPTED stays on screen before the summary
const GUESS_CLEAR_MS = 280;       // empty field before the next guess

function BreakIn({ email, leaked, mailbox, onContinue }: { email: string; leaked: string; mailbox: string; onContinue: () => void }) {
  const attempts = useMemo<Attempt[]>(() => [
    { pw: leaked, ok: false, src: "streaming password from the news" },
    { pw: "BestSummer2027!", ok: false, src: "same words, wrong year" },
    { pw: "Biscuit2004", ok: false, src: "pet + birth year" },
    { pw: "Lakers2004", ok: false, src: "team + birth year" },
    { pw: mailbox, ok: true, src: "same words as the leak, this year" },
  ], [leaked, mailbox]);

  const [phase, setPhase] = useState<GuessPhase>("idle");
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState(0);
  const [done, setDone] = useState<Attempt[]>([]);
  const [cmd, setCmd] = useState("");
  const [cmdHint, setCmdHint] = useState(false);
  const sfx = useRef<Sfx | null>(null);
  const logRef = useRef<HTMLOListElement | null>(null);

  const cur = attempts[Math.min(idx, attempts.length - 1)];

  // Called from the Run click or from typing "run" + Enter, so the audio
  // context is created and resumed inside a user gesture.
  const start = () => {
    if (phase !== "idle") return;
    if (!sfx.current) sfx.current = createSfx();
    setCmdHint(false);
    setIdx(0);
    setTyped(0);
    setDone([]);
    setPhase("typing");
  };
  const runCommand = () => {
    if (cmd.trim().toLowerCase() === "run") start();
    else setCmdHint(true);
  };

  useEffect(() => () => {
    sfx.current?.close();
    sfx.current = null;
  }, []);

  // One timer per step. Every transition lives here so unmounting cancels it.
  useEffect(() => {
    if (phase === "idle" || phase === "won") return;
    let t: number;
    if (phase === "typing") {
      if (typed < cur.pw.length) {
        t = window.setTimeout(() => {
          sfx.current?.key();
          setTyped(typed + 1);
        }, typed === 0 ? GUESS_FIRST_KEY_MS : GUESS_KEY_MS + Math.random() * 18);
      } else {
        t = window.setTimeout(() => {
          sfx.current?.enter();
          setPhase("checking");
        }, GUESS_SUBMIT_MS);
      }
    } else if (phase === "checking") {
      t = window.setTimeout(() => {
        if (cur.ok) sfx.current?.win(); else sfx.current?.deny();
        setDone((d) => [...d, cur]);
        setPhase("result");
      }, GUESS_CHECK_MS);
    } else if (phase === "result") {
      t = window.setTimeout(() => {
        if (cur.ok) {
          setPhase("won");
        } else {
          setTyped(0);
          setPhase("clearing");
        }
      }, cur.ok ? GUESS_WIN_HOLD_MS : GUESS_DENY_HOLD_MS);
    } else {
      t = window.setTimeout(() => {
        setIdx((i) => i + 1);
        setPhase("typing");
      }, GUESS_CLEAR_MS);
    }
    return () => window.clearTimeout(t);
  }, [phase, idx, typed, cur]);

  // Keep the newest log line in view.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [done.length, phase, idx]);

  const live = phase === "typing" || phase === "checking";
  const showText = live || phase === "result" || phase === "won";
  const fieldText = showText ? cur.pw.slice(0, typed) : "";
  const verdict: "ok" | "no" | null = phase === "result" || phase === "won" ? (cur.ok ? "ok" : "no") : null;
  const cardState = verdict === "ok" ? "is-ok" : verdict === "no" ? "is-denied" : phase === "checking" ? "is-checking" : phase === "typing" ? "is-typing" : "";
  const keys = done.reduce((n, a) => n + a.pw.length, 0) + (live ? typed : 0);
  const progress = Math.round((done.length / attempts.length) * 100);
  const stateLabel =
    phase === "idle" ? "ready" :
    phase === "typing" ? "typing" :
    phase === "checking" ? "waiting for the bank" :
    phase === "clearing" ? "next guess" :
    verdict === "ok" ? "access gained" : phase === "won" ? "access gained" : "denied";
  const statusText =
    phase === "idle" ? "Waiting for the tool to start." :
    phase === "typing" ? "The tool is typing the password." :
    phase === "checking" ? "Checking the password…" :
    phase === "clearing" ? "The tool clears the field and tries its next guess." :
    verdict === "ok" || phase === "won" ? "ACCEPTED. Signed in as Alex Morgan." :
    "DENIED. That password was not accepted.";

  return (
    <div className="bk-app bkg">
      <div className="bk-app-bar">
        <span className="bk-app-dot" />
        <span className="bk-app-title">{tx("Guesser — trying passwords on the bank login")}</span>
      </div>
      <div className="bk-app-body">
        <section className="bkg-term" aria-label={tx("Guesser tool")}>
          <div className="bkg-term-head">
            <span className="bkg-term-name">{tx("guesser")}</span>
            <span className="bkg-term-right">
              {!audio.isEnabled() && <span className="bkg-term-mute" title={tx("Turn on Sound & music to hear the keys")}>{tx("sound off")}</span>}
              <span className="bkg-term-state">{stateLabel}</span>
            </span>
          </div>
          <div className="bkg-term-body">
            <div className="bkg-kv"><span>{tx("target")}</span><b>{tx("pay.asterbank.example")}</b></div>
            {phase === "idle" && (
              <>
                <div className="bkg-kv"><span>{tx("account")}</span><b>{email}</b></div>
                <div className="bkg-kv"><span>{tx("inputs")}</span><b>{tx("the news leak + Maya's public profile")}</b></div>
                <div className="bkg-kv"><span>{tx("queue")}</span><b>{txr("{length} guesses", { length: attempts.length })}</b></div>
              </>
            )}
            {phase === "idle" ? (
              <>
                <p className="bkg-note">{tx("The tool types each guess into the bank login, one key at a time. Press Run, or type run.")}</p>
                <form className="bkg-cmd" onSubmit={(e) => { e.preventDefault(); runCommand(); }}>
                  <span className="bkg-prompt">&gt;</span>
                  <input
                    value={cmd}
                    onChange={(e) => { setCmd(e.target.value); setCmdHint(false); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runCommand(); } }}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus
                    data-1p-ignore="true"
                    data-lpignore="true"
                    aria-label={tx("Type run")}
                    placeholder={tx("run")}
                  />
                  <button type="button" className="bkg-run cf-pulse-target" onClick={start}>{tx("Run")}</button>
                </form>
                {cmdHint && <p className="bkg-hint">{tx("Unknown command. Type run, or press the Run button.")}</p>}
              </>
            ) : (
              <p className="bkg-echo"><span className="bkg-prompt">&gt;</span> {tx("run")}</p>
            )}
            <ol className="bkg-log" ref={logRef}>
              {done.map((a, i) => (
                <li key={a.pw} className={`bkg-att ${a.ok ? "ok" : "no"}`}>
                  <span className="bkg-att-n">{i + 1}/{attempts.length}</span>
                  <span className="bkg-att-pw">{a.pw}</span>
                  <span className="bkg-att-v">{a.ok ? tx("ACCEPTED") : tx("DENIED")}</span>
                  <span className="bkg-att-why">{a.src}</span>
                </li>
              ))}
              {live && (
                <li className="bkg-att live">
                  <span className="bkg-att-n">{idx + 1}/{attempts.length}</span>
                  <span className="bkg-att-pw">{fieldText}<i className="bkg-caret" /></span>
                  <span className="bkg-att-v">{phase === "typing" ? tx("typing") : tx("checking")}</span>
                </li>
              )}
            </ol>
          </div>
          <div className="bkg-term-foot">
            <div className="bkg-bar" aria-hidden="true"><i style={{ width: `${progress}%` }} /></div>
            <div className="bkg-stats">
              <span>{txr("guess {length} of {length2}", { length: Math.min(done.length + (live ? 1 : 0), attempts.length), length2: attempts.length })}</span>
              <span>{txr("{keys} keys typed", { keys })}</span>
              <span>{txr("{length} denied", { length: done.filter((a) => !a.ok).length })}</span>
            </div>
          </div>
        </section>

        <section className="bkg-bank" aria-label={tx("Aster Bank payment login")}>
          <div className="bkg-urlbar">
            <span className="bkg-lock"><Ico name="lock" /></span>
            <span className="bkg-url">{tx("pay.asterbank.example")}</span>
            <span className="bkg-auto">{tx("typed by Guesser")}</span>
          </div>
          <div className="bkg-site">
            <div className="bkg-site-head">
              <span className="bkg-logo">{tx("A")}</span>
              <b>{tx("Aster Bank")}</b>
              <em>{tx("Payments")}</em>
            </div>
            <div className="bkg-stage">
              <div className={`bkg-card ${cardState}`}>
                {verdict && <span className={`bkg-stamp ${verdict}`}>{verdict === "ok" ? tx("ACCEPTED") : tx("DENIED")}</span>}
                <h3>{tx("Payment sign-in")}</h3>
                <div className="bkg-lab">
                  <span>{tx("Employee account")}</span>
                  <div className="bkg-field is-ro">{email}</div>
                </div>
                <div className="bkg-lab">
                  <span>{tx("Password")}</span>
                  <div className={`bkg-field ${cardState}`} role="textbox" aria-readonly="true" aria-label={tx("Password typed by the tool")}>
                    {fieldText ? <span className="bkg-typed">{fieldText}</span> : <span className="bkg-ph">{tx("Password")}</span>}
                    {phase === "typing" && <i className="bkg-caret" />}
                  </div>
                </div>
                <div className={`bkg-signin ${phase === "checking" ? "is-press" : ""} ${verdict === "no" ? "is-denied" : ""} ${verdict === "ok" ? "is-ok" : ""}`} aria-hidden="true">
                  {phase === "checking" ? <><i className="bkg-spin" />{tx("Checking")}</> : verdict === "ok" ? tx("Signed in") : tx("Sign in")}
                </div>
                <p className={`bkg-status ${verdict === "no" ? "is-denied" : verdict === "ok" ? "is-ok" : ""}`} role="status" aria-live="polite">
                  {statusText}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {phase === "won" && (
        <div className="bkg-win" role="dialog" aria-label={tx("What this attack shows")}>
          <div className="bkg-win-card bkg-learn">
            <p className="bkg-win-kick">{tx("You are inside the bank")}</p>
            <h2>{tx("What this shows")}</h2>
            <ul className="bkg-learn-list">
              <li>
                <strong>{tx("Do not trust an email just because the account is trusted.")}</strong>
                <span>{tx("The salary message came from Alex's real mailbox, after that mailbox was already taken over. When a message asks you to sign in, stop. Read the address, and open the real site yourself.")}</span>
              </li>
              <li>
                <strong>{tx("A password pattern is easy to work out.")}</strong>
                <span>{txr("{leaked} from the news became {mailbox} on the mailbox and the bank. Changing the year did not make a new password.", { leaked, mailbox })}</span>
              </li>
              <li>
                <strong>{tx("Do not build a password from anything personal.")}</strong>
                <span>{tx("Maya's Facebook page is public. A pet, a birthday, and a favourite team were enough to build guesses such as Biscuit2004. If strangers can see it, they can try it.")}</span>
              </li>
              <li>
                <strong>{tx("A strong password still fails on the wrong page.")}</strong>
                <span>{tx("Dan's password was long and random. He typed it into the salary page, so guessing never mattered. Opening the email did not hand it over. Typing it in did.")}</span>
              </li>
            </ul>
            <button type="button" className="bk-btn bk-btn--primary" autoFocus onClick={onContinue}>{tx("Take the final quiz")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
